const DB_NAME = 'fort';
const DB_VERSION = 2;
const STORE_NAME = 'snapshots';

export type SnapshotEntity = 'fixtures' | 'orders';

export interface SnapshotRecord {
    blob: Blob;
    etag: string;
}

interface StoredRecord {
    key: string;
    blob: Blob;
    etag: string;
}

let openDbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
    if (openDbPromise) return openDbPromise;

    openDbPromise = new Promise<IDBDatabase | null>((resolve) => {
        if (typeof indexedDB === 'undefined') {
            resolve(null);
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = request.result;

            // v1 stored parsed object arrays; v2 stored NDJSON strings — drop and recreate to store Blobs.
            if (
                (event.oldVersion ?? 0) < 2 &&
                db.objectStoreNames.contains(STORE_NAME)
            ) {
                db.deleteObjectStore(STORE_NAME);
            }

            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'key' });
            }
        };

        request.onsuccess = () => {
            const db = request.result;

            db.onclose = () => {
                openDbPromise = null;
            };

            db.onerror = () => {
                openDbPromise = null;
                db.close();
            };

            resolve(db);
        };

        request.onerror = () => {
            openDbPromise = null;
            resolve(null);
        };

        request.onblocked = () => {
            openDbPromise = null;
            resolve(null);
        };
    });

    return openDbPromise;
}

function makeKey({
    entity,
    datasetId,
    numberOfYears,
}: {
    entity: SnapshotEntity;
    datasetId: number;
    numberOfYears: number;
}): string {
    return `${entity}-${datasetId}-${numberOfYears}`;
}

async function getSnapshotRecordByKey(
    key: string
): Promise<SnapshotRecord | null> {
    const db = await openDb();
    if (!db) return null;

    return new Promise<SnapshotRecord | null>((resolve) => {
        try {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(key);

            request.onsuccess = () => {
                const record = request.result as StoredRecord | undefined;
                resolve(
                    record ? { blob: record.blob, etag: record.etag } : null
                );
            };
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
}

async function putSnapshotRecordByKey(
    key: string,
    { blob, etag }: { blob: Blob; etag: string }
): Promise<void> {
    const db = await openDb();
    if (!db) return;

    return new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const record: StoredRecord = {
                key,
                blob,
                etag,
            };
            store.put(record);

            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
            tx.onabort = () => resolve();
        } catch {
            resolve();
        }
    });
}

export async function getSnapshotRecord({
    entity,
    datasetId,
    numberOfYears,
}: {
    entity: SnapshotEntity;
    datasetId: number;
    numberOfYears: number;
}): Promise<SnapshotRecord | null> {
    const key = makeKey({ entity, datasetId, numberOfYears });
    return await getSnapshotRecordByKey(key);
}

export async function putSnapshotRecord({
    entity,
    datasetId,
    numberOfYears,
    blob,
    etag,
}: {
    entity: SnapshotEntity;
    datasetId: number;
    numberOfYears: number;
    blob: Blob;
    etag: string;
}): Promise<void> {
    const key = makeKey({ entity, datasetId, numberOfYears });
    await putSnapshotRecordByKey(key, { blob, etag });
}
